/**
 * OKR Graph Builder Utility
 * Builds ReactFlow graph from OKR hierarchy using dagre layout algorithm
 */

import dagre from 'dagre';
import { Node, Edge } from 'reactflow';
import { OKR } from '../types/okr';

interface GraphBuilderOptions {
  direction?: 'TB' | 'LR'; // Top-to-bottom or Left-to-right
  nodeSpacing?: number;
  rankSpacing?: number;
}

interface OKRGraphResult {
  nodes: Node[];
  edges: Edge[];
}

// Base node dimensions (without Key Results)
const BASE_NODE_DIMENSIONS = {
  company: { width: 280, height: 140 },
  team: { width: 240, height: 120 },
  department: { width: 240, height: 120 },
  individual: { width: 200, height: 100 },
};

// Calculate node height based on number of key results
const calculateNodeHeight = (okr: OKR): number => {
  const baseDimensions = BASE_NODE_DIMENSIONS[okr.type] || BASE_NODE_DIMENSIONS.individual;
  const keyResults = okr.keyResults || [];

  if (keyResults.length === 0) {
    return baseDimensions.height;
  }

  // Show max 3 KRs, each KR adds ~20px (title + progress bar + spacing)
  const displayKRs = Math.min(keyResults.length, 3);
  const krSectionHeight = 30 + (displayKRs * 20) + (keyResults.length > 3 ? 10 : 0); // header + KRs + "more" indicator

  return baseDimensions.height + krSectionHeight;
};

/**
 * Build OKR hierarchy graph with automatic layout
 */
export function buildOKRGraph(
  okrs: OKR[],
  options: GraphBuilderOptions = {}
): OKRGraphResult {
  const {
    direction = 'TB',
    nodeSpacing = 50,
    rankSpacing = 100,
  } = options;

  // Handle empty case
  if (!okrs || okrs.length === 0) {
    return { nodes: [], edges: [] };
  }

  // Initialize dagre graph
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: direction,
    nodesep: nodeSpacing,
    ranksep: rankSpacing,
    marginx: 20,
    marginy: 20,
  });

  // Calculate child counts for each OKR
  const childCountMap = new Map<string, number>();
  okrs.forEach((okr) => {
    if (okr.parentOKRId) {
      const currentCount = childCountMap.get(okr.parentOKRId) || 0;
      childCountMap.set(okr.parentOKRId, currentCount + 1);
    }
  });

  // Add nodes to dagre graph with dynamic dimensions based on KR count
  okrs.forEach((okr) => {
    const baseDimensions = BASE_NODE_DIMENSIONS[okr.type] || BASE_NODE_DIMENSIONS.individual;
    dagreGraph.setNode(okr.id, {
      width: baseDimensions.width,
      height: calculateNodeHeight(okr),
    });
  });

  // A parent that is not in `okrs` (filtered out, or not readable by this user) gets no
  // edge: dagre would invent a zero-size phantom node for it and skew the layout, and
  // ReactFlow drops an edge whose source node does not exist anyway.
  const loadedIds = new Set(okrs.map((okr) => okr.id));
  const hasLoadedParent = (okr: OKR): okr is OKR & { parentOKRId: string } =>
    !!okr.parentOKRId && loadedIds.has(okr.parentOKRId);

  // Add edges to dagre graph (parent -> child relationships)
  okrs.forEach((okr) => {
    if (hasLoadedParent(okr)) {
      dagreGraph.setEdge(okr.parentOKRId, okr.id);
    }
  });

  // Run dagre layout algorithm
  dagre.layout(dagreGraph);

  // Convert dagre nodes to ReactFlow nodes
  const nodes: Node[] = okrs.map((okr) => {
    const dagreNode = dagreGraph.node(okr.id);
    const childCount = childCountMap.get(okr.id) || 0;

    // dagre gives center position, ReactFlow uses top-left
    const position = {
      x: dagreNode.x - dagreNode.width / 2,
      y: dagreNode.y - dagreNode.height / 2,
    };

    return {
      id: okr.id,
      type: okr.type,
      data: {
        okr,
        childCount: childCount > 0 ? childCount : undefined,
      },
      position,
    };
  });

  // Convert dagre edges to ReactFlow edges
  const edges: Edge[] = okrs
    .filter(hasLoadedParent)
    .map((okr) => {
      const parentOKR = okrs.find((parent) => parent.id === okr.parentOKRId);
      const isCompanyToTeam = parentOKR?.type === 'company';

      return {
        id: `e-${okr.parentOKRId}-${okr.id}`,
        source: okr.parentOKRId!,
        target: okr.id,
        type: 'smoothstep',
        animated: isCompanyToTeam, // Animate top-level connections
      };
    });

  return { nodes, edges };
}

/**
 * Filter OKRs by type for view toggles
 */
export function filterOKRsByType(
  okrs: OKR[],
  visibleTypes: Set<string>
): OKR[] {
  if (visibleTypes.size === 0) {
    return okrs;
  }

  return okrs.filter((okr) => visibleTypes.has(okr.type));
}

/**
 * Get root OKRs (no parent)
 */
export function getRootOKRs(okrs: OKR[]): OKR[] {
  return okrs.filter((okr) => !okr.parentOKRId);
}

/**
 * Get children of a specific OKR
 */
export function getChildrenOKRs(okrs: OKR[], parentId: string): OKR[] {
  return okrs.filter((okr) => okr.parentOKRId === parentId);
}

/**
 * Get all descendants of an OKR (recursive)
 */
export function getDescendantOKRs(okrs: OKR[], parentId: string): OKR[] {
  const directChildren = getChildrenOKRs(okrs, parentId);
  const descendants = [...directChildren];

  directChildren.forEach((child) => {
    descendants.push(...getDescendantOKRs(okrs, child.id));
  });

  return descendants;
}

/**
 * Get path from root to specific OKR
 */
export function getOKRPath(okrs: OKR[], okrId: string): OKR[] {
  const path: OKR[] = [];
  let currentOKR = okrs.find((okr) => okr.id === okrId);

  while (currentOKR) {
    path.unshift(currentOKR);
    if (!currentOKR.parentOKRId) break;
    currentOKR = okrs.find((okr) => okr.id === currentOKR!.parentOKRId);
  }

  return path;
}
